import React, { useEffect, useRef, useState } from "react";

import * as d3 from "d3";

import AccountPeekOverlay from "./AccountPeekOverlay";
import GraphCenterHoverCard from "./GraphCenterHoverCard";
import { getCenterNodeInitials } from "./relationshipGraphUtils";
import {
  clampLevel,
  formatAggregateRl,
  getAccountIdentityColor,
  getCoverageMatrixDotColor,
  RL_DOT_COLORS,
} from "../account-relationships/relationshipUtils";

const RL_BAND_MODERATE_COLOR = "#eab308";

function getL0DotFill(rlAggregate) {
  if (rlAggregate === null || rlAggregate === undefined) {
    return "url(#t3-l0-hatch-null)";
  }
  return getCoverageMatrixDotColor(rlAggregate);
}

function getLabelText(name, count) {
  const label = String(name || "");
  if (count > 18)
    return label
      .replace(/[^A-Za-z0-9]/g, "")
      .slice(0, 2)
      .toUpperCase();
  if (count > 10) return label.length <= 9 ? label : `${label.slice(0, 8)}...`;
  return label.length <= 16 ? label : `${label.slice(0, 15)}...`;
}

function getMapGeometry(count) {
  if (count > 18) {
    return { dotR: 7, labelFont: 9, sectorOpacity: 0.025 };
  }
  if (count > 10) {
    return { dotR: 9, labelFont: 10, sectorOpacity: 0.035 };
  }
  return { dotR: 12, labelFont: 11, sectorOpacity: 0.045 };
}

function getRadialDistance(rlAggregate, baseR) {
  if (rlAggregate === null || rlAggregate === undefined) return baseR * 0.92;
  const level = clampLevel(rlAggregate);
  return baseR * (1 - (level / 5) * 0.72);
}

/**
 * L0 portfolio ego map — "You" at center, one dot per account, radial distance by aggregate RL.
 */
export default function MyRelationshipsL0Map({
  accounts,
  considerMeddpiccRole = false,
  activeAccountId = null,
  selectedAccountIds = [],
  onSelectAccount,
  onClearPeek,
  onOpenAccount,
  onAssessRingContact,
  userName,
  centerUserStakeholder,
  sharedAccountIds = [],
  isViewingTeamMember = false,
  peekAccounts,
  peekUserName,
  peekCenterUserStakeholder,
  fanOutCap,
}) {
  const hostRef = useRef(null);
  const [youHovered, setYouHovered] = useState(false);
  const peekPool = peekAccounts || accounts;
  const activeAccount = (peekPool || []).find(
    account => account.id === activeAccountId
  );

  useEffect(() => {
    if (!activeAccountId || !onClearPeek) return undefined;
    const handleKeyDown = event => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClearPeek();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeAccountId, onClearPeek]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    const render = () => {
      const width = host.clientWidth || 520;
      const height = Math.max(420, Math.min(560, host.clientHeight || 500));
      const legendHeight = 54;
      const usableHeight = Math.max(320, height - legendHeight);
      const list = accounts || [];

      d3.select(host).html("");

      if (list.length === 0) {
        d3.select(host)
          .append("div")
          .attr("class", "t3-l2-empty")
          .html(
            "<b>No matching accounts</b><span>Adjust search or clear filters to restore the portfolio map.</span>"
          );
        return;
      }

      const n = list.length;
      const geom = getMapGeometry(n);
      const cx = width / 2;
      const cy = usableHeight / 2;
      const baseR = Math.max(120, Math.min(cx, cy) - 68);
      const sectorAngle = (2 * Math.PI) / n;
      const svg = d3
        .select(host)
        .append("svg")
        .attr("width", width)
        .attr("height", usableHeight)
        .attr("class", "t3-l0-svg")
        .style("position", "absolute")
        .style("inset", "0 auto auto 0");

      const defs = svg.append("defs");
      defs.html(`
        <pattern id="t3-l0-hatch-null" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <rect width="6" height="6" fill="#f1f5f9"></rect>
          <line x1="0" y1="0" x2="0" y2="6" stroke="#94a3b8" stroke-width="1.5"></line>
        </pattern>
      `);

      [5, 3, 1].forEach(rl => {
        const ringR = getRadialDistance(rl, baseR);
        svg
          .append("circle")
          .attr("cx", cx)
          .attr("cy", cy)
          .attr("r", ringR)
          .attr("class", `t3-l0-rl-guide rl-${rl}`);
        svg
          .append("text")
          .attr("x", cx + ringR + 4)
          .attr("y", cy + 3)
          .attr("class", `t3-l0-rl-guide-lbl rl-${rl}`)
          .text(`RL ${rl}`);
      });

      const outerR = baseR + 26;
      list.forEach((account, index) => {
        const startAngle = index * sectorAngle - Math.PI / 2 - sectorAngle / 2;
        const endAngle = startAngle + sectorAngle;
        const x1 = cx + Math.cos(startAngle) * outerR;
        const y1 = cy + Math.sin(startAngle) * outerR;
        const x2 = cx + Math.cos(endAngle) * outerR;
        const y2 = cy + Math.sin(endAngle) * outerR;
        const largeArc = sectorAngle > Math.PI ? 1 : 0;

        svg
          .append("path")
          .attr(
            "d",
            `M${cx},${cy} L${x1},${y1} A${outerR},${outerR} 0 ${largeArc},1 ${x2},${y2} Z`
          )
          .attr(
            "fill",
            account.sectorColor || getAccountIdentityColor(account.id)
          )
          .attr("opacity", geom.sectorOpacity);

        svg
          .append("line")
          .attr("x1", cx)
          .attr("y1", cy)
          .attr("x2", x1)
          .attr("y2", y1)
          .attr("stroke", "#cbd5e1")
          .attr("stroke-width", 0.6)
          .attr("opacity", n > 15 ? 0.25 : 0.45);
      });

      list.forEach((account, index) => {
        const angle = index * sectorAngle - Math.PI / 2;
        const distance = getRadialDistance(account.rlAggregate, baseR);
        const stopMargin = geom.dotR + 4;
        const stopDistance = Math.max(0, distance - stopMargin);
        const x = cx + Math.cos(angle) * stopDistance;
        const y = cy + Math.sin(angle) * stopDistance;
        const isDimmed = activeAccountId && activeAccountId !== account.id;

        svg
          .append("line")
          .attr("x1", cx)
          .attr("y1", cy)
          .attr("x2", x)
          .attr("y2", y)
          .attr(
            "stroke-width",
            Math.max(1, 1 + Math.min(Number(account.mappedPairs) || 0, 12) / 3)
          )
          .attr("class", `t3-l0-spoke-fan${isDimmed ? " dimmed" : ""}`);
      });

      const youRadius = n > 15 ? 18 : 22;
      const youNode = svg.append("g");
      youNode
        .append("circle")
        .attr("cx", cx)
        .attr("cy", cy)
        .attr("r", youRadius)
        .attr("fill", "#1e293b")
        .attr("stroke", "#fff")
        .attr("stroke-width", 2);
      youNode
        .style("cursor", "pointer")
        .on("mouseenter", () => setYouHovered(true))
        .on("mouseleave", () => setYouHovered(false));
      youNode
        .append("text")
        .attr("x", cx)
        .attr("y", cy + 4)
        .attr("text-anchor", "middle")
        .attr("fill", "#fff")
        .attr("font-size", n > 15 ? 10 : 11)
        .attr("font-weight", 700)
        .text(getCenterNodeInitials(userName));

      list.forEach((account, index) => {
        const angle = index * sectorAngle - Math.PI / 2;
        const distance = getRadialDistance(account.rlAggregate, baseR);
        const dx = cx + Math.cos(angle) * distance;
        const dy = cy + Math.sin(angle) * distance;
        const isActive = activeAccountId === account.id;
        const isSelected = selectedAccountIds.includes(account.id);
        const isDimmed = activeAccountId && !isActive;
        const isShared =
          isViewingTeamMember && sharedAccountIds.includes(account.id);
        const dotFill = getL0DotFill(account.rlAggregate);
        const node = svg
          .append("g")
          .attr(
            "class",
            `t3-l0-dot-group${isSelected ? " selected" : ""}${isActive ? " peek" : ""}${isDimmed ? " dimmed" : ""}${isShared ? " shared" : ""}`
          )
          .style("cursor", "pointer")
          .on("click", event => onSelectAccount?.(account.id, event));

        node
          .append("circle")
          .attr("cx", dx)
          .attr("cy", dy)
          .attr("r", geom.dotR + 8)
          .attr("fill", "none")
          .attr("stroke", isActive ? "#f59e0b" : "#9333ea")
          .attr("stroke-width", isActive ? 3 : 2.5)
          .attr("class", "t3-l0-halo");

        node
          .append("circle")
          .attr("cx", dx)
          .attr("cy", dy)
          .attr("r", geom.dotR)
          .attr("fill", dotFill)
          .attr("stroke", "#fff")
          .attr("stroke-width", 2);

        const labelOffset = n > 15 && index % 2 === 1 ? 14 : 0;
        const labelDistance =
          baseR + (n > 18 ? 22 : n > 10 ? 36 : 42) + labelOffset;
        const lx = cx + Math.cos(angle) * labelDistance;
        const ly = cy + Math.sin(angle) * labelDistance;
        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle);

        svg
          .append("text")
          .attr("x", lx)
          .attr("y", ly)
          .attr(
            "text-anchor",
            Math.abs(cosA) < 0.15 ? "middle" : cosA > 0 ? "start" : "end"
          )
          .attr(
            "dominant-baseline",
            Math.abs(sinA) < 0.15 ? "middle" : sinA > 0 ? "hanging" : "auto"
          )
          .attr("class", "t3-l0-acct-lbl")
          .attr("font-size", geom.labelFont)
          .attr("opacity", isDimmed ? 0.4 : 1)
          .attr("fill", isActive ? "#92400e" : undefined)
          .attr("font-weight", isActive ? 700 : undefined)
          .text(getLabelText(account.name, n));

        node
          .append("title")
          .text(
            `${account.name} - ${formatAggregateRl(account.rlAggregate)}\nMapped ${Number(account.mappedPairs) || 0} · Assessed ${Number(account.assessedPairs) || 0} · Pending ${Number(account.pendingAssessments) || 0}`
          );
      });

      d3
        .select(host)
        .append("div")
        .attr("class", "t3-l0-legend t3-l0-legend--portfolio")
        .style("position", "absolute")
        .style("left", 0)
        .style("right", 0)
        .style("bottom", 0)
        .style("height", `${legendHeight}px`)
        .style("background", "rgba(255,255,255,0.92)")
        .style("border-top", "1px solid var(--border)")
        .style("box-sizing", "border-box").html(`
          <div class="t3-l0-legend-row">
            <span class="lg-item"><b>Distance</b> = team RL (account)</span>
            <span class="lg-item"><span class="lg-bar"></span><b>Spoke thickness</b> = team fan-out</span>
          </div>
          <div class="t3-l0-legend-row">
            <span class="lg-item">
              <span class="lg-swatch" style="background:${RL_DOT_COLORS[1]}"></span>Poor (0–1)
              <span class="lg-swatch" style="background:${RL_BAND_MODERATE_COLOR}"></span>Moderate (2–3)
              <span class="lg-swatch" style="background:${RL_DOT_COLORS[4]}"></span>Strong (4–5)
              <span class="lg-swatch lg-swatch--hatch" title="Unassessed"></span>RL ?
            </span>
          </div>
        `);
    };

    render();

    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(render) : null;
    observer?.observe(host);

    return () => {
      observer?.disconnect();
      d3.select(host).selectAll("*").remove();
    };
  }, [
    accounts,
    activeAccountId,
    selectedAccountIds,
    onSelectAccount,
    userName,
    sharedAccountIds,
    isViewingTeamMember,
  ]);

  const youInitials = getCenterNodeInitials(userName);

  return (
    <div
      id="t3-map-container"
      className="t3-map-container"
      style={{
        width: "100%",
        height: "100%",
        minHeight: 440,
        position: "relative",
      }}
    >
      <div ref={hostRef} style={{ position: "absolute", inset: 0 }} />
      <GraphCenterHoverCard
        visible={youHovered}
        centerUserStakeholder={centerUserStakeholder}
        initials={youInitials}
      />
      {activeAccount ? (
        <AccountPeekOverlay
          account={activeAccount}
          considerMeddpiccRole={considerMeddpiccRole}
          onClose={onClearPeek}
          onOpenAccount={onOpenAccount}
          onAssessRingContact={onAssessRingContact}
          userName={peekUserName !== undefined ? peekUserName : userName}
          centerUserStakeholder={
            peekCenterUserStakeholder !== undefined
              ? peekCenterUserStakeholder
              : centerUserStakeholder
          }
          viewerLabel={peekAccounts ? peekUserName || undefined : undefined}
          fanOutCap={fanOutCap}
        />
      ) : null}
    </div>
  );
}
